import React from 'react'
import { Typography, Container, Box, CircularProgress } from '@mui/material';
import { withStyles } from '@mui/styles'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'

import { pageDisplayerStyles } from './page-displayer.styles'

/**
 * @typedef {object} PageDisplayerProps
 * @property {boolean} [loading=false] - Si es `true`, muestra un spinner de carga centrado.
 * @property {Error | false} [error=false] - Si es un objeto Error, muestra el mensaje de error centrado.
 * @property {function(): void} handleGoBack - La función callback que se ejecuta al hacer clic en el botón "Volver".
 * @property {string} [textGoBack='Back'] - El texto del botón "Volver".
 * @property {'xs' | 'sm' | 'md' | 'lg' | 'xl' | false} [maxWidth='lg'] - El ancho máximo del contenedor.
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {React.ReactNode} children - El contenido de la página.
 * @property {[string, string]} [padding=['15px', '20px 40px']] - El padding del contenedor de contenido [mobile, desktop].
 */

/**
 * Un componente de layout para páginas con estados de carga, error y navegación.
 * 
 * Proporciona una estructura consistente para páginas con un botón de "Volver",
 * manejo de estados de carga y error, y un contenedor con padding configurable.
 * 
 * @component
 * @example
 * const MyDetailPage = () => {
 *   const [loading, setLoading] = useState(true);
 *   const [error, setError] = useState(false);
 *   const navigate = useNavigate();
 * 
 *   useEffect(() => {
 *     fetchData()
 *       .then(() => setLoading(false))
 *       .catch((err) => setError(err));
 *   }, []);
 * 
 *   return (
 *     <PageDisplayer
 *       loading={loading}
 *       error={error}
 *       handleGoBack={() => navigate(-1)}
 *       textGoBack="Volver a la lista"
 *       maxWidth="md"
 *     >
 *       <h1>Detalles del Item</h1>
 *       <p>Contenido de la página...</p>
 *     </PageDisplayer>
 *   );
 * };
 * 
 * @param {PageDisplayerProps} props - Las props del componente PageDisplayer.
 */
export const PageDisplayer = withStyles(pageDisplayerStyles)(
  ({
    loading = false,
    error = false,
    handleGoBack,
    textGoBack = 'Back',
    maxWidth = 'lg',
    classes,
    children,
    padding = ['15px', '20px 40px'],
  }) => {
    if (loading) {
      return (
        <Box display="flex" height="100%" justifyContent="center" alignItems="center">
          <CircularProgress size={40} />
        </Box>
      )
    }
    if (error) {
      return (
        <Box display="flex" height="100%" justifyContent="center" alignItems="center">
          <p>{error.message}</p>
        </Box>
      )
    }
    return (
      <Container className={classes.root} maxWidth={maxWidth}>
        <Box height="100%" mt={[2, 4]}>
          <div className={classes.return} onClick={handleGoBack}>
            <ArrowBackIos fontSize="inherit" />
            <Typography variant="body1">{textGoBack}</Typography>
          </div>
          <Box bgcolor="common.white" p={padding}>
            {children}
          </Box>
        </Box>
      </Container>
    )
  }
)

export default PageDisplayer
